import { css } from '@emotion/react';

import { KAI_SHIKI_TYPOGRAPHY, styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideSpacing } from '@kaiizen/kai-shiki/core';

export const TURN_BASED_DASHBOARD_PROJECT_ROW_STYLES = {
  projectCard: css`
    label: TURN_BASED_DASHBOARD_PROJECT_CARD;
    display: grid;
    grid-template-columns: calc(${styleguideSpacing.s8}px * 3 + ${styleguideSpacing.s2}px) minmax(0, 1fr);
    border: 1px solid ${styleguideColors.stroke};
    background: ${styleguideColors.paper};
    cursor: pointer;

    &:hover {
      border-color: ${styleguideColors.ink};
    }

    @media (max-width: 860px) {
      grid-template-columns: 1fr;
    }
  `,
  photoFrame: css`
    label: TURN_BASED_DASHBOARD_PHOTO_FRAME;
    position: relative;
    overflow: hidden;
    align-self: stretch;
    background: ${styleguideColors.sunken};
    border-right: 1px solid ${styleguideColors.stroke};

    @media (max-width: 860px) {
      height: calc(${styleguideSpacing.s8}px * 2);
      border-right: 0;
      border-bottom: 1px solid ${styleguideColors.stroke};
    }
  `,
  photo: css`
    label: TURN_BASED_DASHBOARD_PHOTO;
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  photoPlaceholder: css`
    label: TURN_BASED_DASHBOARD_PHOTO_PLACEHOLDER;
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${styleguideColors.textTertiary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;
  `,
  projectDetails: css`
    label: TURN_BASED_DASHBOARD_PROJECT_DETAILS;
    display: flex;
    flex-direction: column;
    min-width: 0;
    container-type: inline-size;
    padding: ${styleguideSpacing.s3}px ${styleguideSpacing.s4}px;
  `,
  projectTop: css`
    label: TURN_BASED_DASHBOARD_PROJECT_TOP;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: flex-start;
    gap: ${styleguideSpacing.s3}px;
  `,
  tag: css`
    label: TURN_BASED_DASHBOARD_TAG;
    display: inline-flex;
    align-items: center;
    min-height: calc(${styleguideSpacing.s4}px + ${styleguideSpacing.s1}px);
    padding: 0 ${styleguideSpacing.s2}px;
    border: 1px solid ${styleguideColors.ink};
    border-radius: 0;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    line-height: 1.3;
    text-transform: uppercase;
    white-space: nowrap;
  `,
  jobTag: css`
    label: TURN_BASED_DASHBOARD_JOB_TAG;
    background: ${styleguideColors.midtone};
    border-color: ${styleguideColors.midtone};
    color: ${styleguideColors.white};
  `,
  artifactTag: css`
    label: TURN_BASED_DASHBOARD_ARTIFACT_TAG;
    background: ${styleguideColors.ink};
    color: ${styleguideColors.white};
  `,
  amount: css`
    label: TURN_BASED_DASHBOARD_AMOUNT;
    color: ${styleguideColors.ink};
    text-align: right;
    font-variant-numeric: tabular-nums;
    ${KAI_SHIKI_TYPOGRAPHY.largeBold}
  `,
  amountUnset: css`
    label: TURN_BASED_DASHBOARD_AMOUNT_UNSET;
    color: ${styleguideColors.textTertiary};
    font-weight: ${styleguideFontWeight.regular};
  `,
  projectActions: css`
    label: TURN_BASED_DASHBOARD_PROJECT_ACTIONS;
    display: flex;
    align-items: flex-start;
    gap: ${styleguideSpacing.s1}px;
  `,
  amountLabel: css`
    label: TURN_BASED_DASHBOARD_AMOUNT_LABEL;
    display: block;
    margin-top: ${styleguideSpacing.s1}px;
    color: ${styleguideColors.textTertiary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;
  `,
  addressLine: css`
    label: TURN_BASED_DASHBOARD_ADDRESS_LINE;
    display: flex;
    align-items: baseline;
    gap: ${styleguideSpacing.s1}px;
    min-width: 0;
    ${KAI_SHIKI_TYPOGRAPHY.largeBold}
  `,
  address: css`
    label: TURN_BASED_DASHBOARD_ADDRESS;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  projectName: css`
    label: TURN_BASED_DASHBOARD_PROJECT_NAME;
    min-width: 0;
    overflow: hidden;
    color: ${styleguideColors.textSecondary};
    font-weight: ${styleguideFontWeight.regular};
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  propertyLine: css`
    label: TURN_BASED_DASHBOARD_PROPERTY_LINE;
    display: flex;
    gap: ${styleguideSpacing.s1}px;
    min-width: 0;
    margin-top: ${styleguideSpacing.s1}px;
    color: ${styleguideColors.textTertiary};
    ${KAI_SHIKI_TYPOGRAPHY.small}
  `,
  projectFacts: css`
    label: TURN_BASED_DASHBOARD_PROJECT_FACTS;
    white-space: nowrap;
  `,
  projectMetadata: css`
    label: TURN_BASED_DASHBOARD_PROJECT_METADATA;
    display: grid;
    grid-template-columns:
      minmax(calc(${styleguideSpacing.s8}px * 3 + ${styleguideSpacing.s6}px + ${styleguideSpacing.s2}px), 1.2fr)
      repeat(6, minmax(calc(${styleguideSpacing.s8}px + ${styleguideSpacing.s6}px + ${styleguideSpacing.s2}px), 1fr));
    gap: ${styleguideSpacing.s3}px ${styleguideSpacing.s2}px;
    margin-top: ${styleguideSpacing.s3}px;
    padding-top: ${styleguideSpacing.s2}px;
    border-top: 1px solid ${styleguideColors.stroke};

    @container (max-width: calc(${styleguideSpacing.s8}px * 9 + ${styleguideSpacing.s6}px * 7 + ${styleguideSpacing.s2}px * 13)) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @container (max-width: calc(${styleguideSpacing.s8}px * 3 + ${styleguideSpacing.s6}px * 3 + ${styleguideSpacing.s2}px * 5)) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @container (max-width: calc(${styleguideSpacing.s8}px * 2 + ${styleguideSpacing.s6}px * 2 + ${styleguideSpacing.s2}px * 3)) {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  metadataLabel: css`
    label: TURN_BASED_DASHBOARD_METADATA_LABEL;
    color: ${styleguideColors.textTertiary};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoCaption)};
    font-weight: ${styleguideFontWeight.bold};
    text-transform: uppercase;
  `,
  metadataValue: css`
    label: TURN_BASED_DASHBOARD_METADATA_VALUE;
    overflow: hidden;
    margin-top: ${styleguideSpacing.s1}px;
    color: ${styleguideColors.ink};
    text-overflow: ellipsis;
    white-space: nowrap;
    ${KAI_SHIKI_TYPOGRAPHY.small}
  `,
  metadataTagRow: css`
    label: TURN_BASED_DASHBOARD_METADATA_TAG_ROW;
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-top: ${styleguideSpacing.s1}px;
  `,
  metadataAlert: css`
    label: TURN_BASED_DASHBOARD_METADATA_ALERT;
    color: ${styleguideColors.error};
    font-weight: ${styleguideFontWeight.bold};
  `,
  waitingOnYou: css`
    label: TURN_BASED_DASHBOARD_WAITING_ON_YOU;
    padding: 0 ${styleguideSpacing.s1}px;
    background: ${styleguideColors.accent};
    font-weight: ${styleguideFontWeight.bold};
  `,
  actionMenu: css`
    label: TURN_BASED_DASHBOARD_ACTION_MENU;
    position: relative;
  `,
  actionButton: css`
    label: TURN_BASED_DASHBOARD_ACTION_BUTTON;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${styleguideSpacing.s5}px;
    height: ${styleguideSpacing.s5}px;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: ${styleguideColors.textSecondary};
    cursor: pointer;
    opacity: 0;
    pointer-events: none;

    &:hover {
      background: ${styleguideColors.wash};
      color: ${styleguideColors.ink};
    }

    &:focus-visible {
      outline: 2px solid ${styleguideColors.ink};
      outline-offset: 2px;
    }
  `,
  actionButtonVisible: css`
    label: TURN_BASED_DASHBOARD_ACTION_BUTTON_VISIBLE;
    opacity: 1;
    pointer-events: auto;
  `,
  actionPanel: css`
    label: TURN_BASED_DASHBOARD_ACTION_PANEL;
    position: absolute;
    z-index: 2;
    top: calc(100% + ${styleguideSpacing.s1}px);
    right: 0;
    min-width: calc(${styleguideSpacing.s8}px * 2);
    padding: ${styleguideSpacing.s1}px 0;
    background: ${styleguideColors.paper};
    border: 1px solid ${styleguideColors.stroke};
    box-shadow: 0 ${styleguideSpacing.s1}px ${styleguideSpacing.s3}px ${styleguideColors.strokeFaded};
  `,
  actionMenuItem: css`
    label: TURN_BASED_DASHBOARD_ACTION_MENU_ITEM;
    width: 100%;
    min-height: ${styleguideSpacing.s4}px;
    padding: 0 ${styleguideSpacing.s2}px;
    border: 0;
    background: transparent;
    color: ${styleguideColors.ink};
    cursor: pointer;
    text-align: left;
    ${KAI_SHIKI_TYPOGRAPHY.small}

    &:hover {
      background: ${styleguideColors.wash};
    }
  `,
};
